import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AnalyticsEvent, Course, environment, ISendler, LiquidViewerMedia, Module, User } from 'src/index';

@Injectable({
  providedIn: 'root'
})
export class InternalTrackingService implements ISendler {
  http = inject(HttpClient);
  deviceService = inject(DeviceDetectorService);
  config = { ...environment.analytics };

  convertDate(date: Date) {
    return date.toISOString().substring(0, 16).replace('T', ', ');
  }

  trackRegistration(user: Partial<User>) {
    this.sendEvent(AnalyticsEvent.Registration, {
      user_id: user.uuid,
      registration_date: this.convertDate(new Date()),
      device_type: this.getDeviceType(),
    });
  }

  trackLogin(user: Partial<User>) {
    this.sendEvent(AnalyticsEvent.Login, {
      user_id: user.uuid,
      login_date: this.convertDate(new Date()),
      device_type: this.getDeviceType(),
    });
  }

  trackSelfAssessmentStart(user: Partial<User>) {
    this.sendEvent(AnalyticsEvent.SelfAssessmentStart, {
      user_id: user.uuid,
      self_assessment_start_date: this.convertDate(new Date()),
      device_type: this.getDeviceType(),
    });
  }

  trackSelfAssessmentComplete(user: Partial<User>, startDate: string) {
    this.sendEvent(AnalyticsEvent.SelfAssessmentComplete, {
      user_id: user.uuid,
      self_assessment_start_date: this.convertDate(new Date(startDate)),
      self_assessment_complete_date: this.convertDate(new Date()),
      device_type: this.getDeviceType(),
    });
  }

  trackCourseAccess(user: Partial<User>, course: Partial<Course>) {
    this.sendEvent(AnalyticsEvent.CourseAccess, {
      user_id: user.uuid,
      course_id: course.uuid,
      course_name: course.title,
      access_time: new Date().getTime(),
      device_type: this.getDeviceType(),
    });
  }

  trackModuleAccess(user: Partial<User>, course: Partial<Course>, module: Partial<Module>) {
    this.sendEvent(AnalyticsEvent.ModuleAccess, {
      user_id: user.uuid,
      course_id: course.uuid,
      course_name: course.title,
      module_id: module.id,
      module_name: module.title,
      access_time: new Date().getTime(),
      device_type: this.getDeviceType(),
    });
  }

  trackVideoPlay(user: Partial<User>, course: Partial<Course>, video: LiquidViewerMedia, titleFromCurrentIndex: string) {
    this.sendEvent(AnalyticsEvent.VideoPlay, {
      user_id: user.uuid,
      course_id: course.uuid,
      course_name: course.title,
      video_filename: video.title || titleFromCurrentIndex || '',
      access_time: new Date().getTime(),
      device_type: this.getDeviceType(),
    });
  }

  getDeviceType() {
    return this.deviceService.deviceType;
  }

  sendEvent(name: string, eventData: any): Observable<{ response: string }> {
    if (environment.trackings) {
      firstValueFrom(this.http.post<{ response: string }>(environment.trackings, { name, eventData }).pipe(
        catchError(error => throwError(() => error.error.message)),
      ));

      return of({ response: 'OK' });
    }

    console.warn('Analytics send event missing', this.config.eventsMainCategory, name, eventData);
    return of({ response: 'KO' });
  }
}
